// ═══ SWING PLAYS MODAL — "Tomorrow's Swing Plays" (iter 100) ═════════════════
// Ranks 16 tickers by swingScore and shows bias pills + entry zone + stop +
// target + rationale. Powered by GET /api/swing/next-day.
import { useEffect, useState } from "react";
import axios from "axios";
import { hapticTap } from "../../utils/haptics";
import { Skel } from "./Skel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BIAS_STYLES = {
  LONG:          { color: "#4ade80", bg: "rgba(74,222,128,0.12)", border: "rgba(74,222,128,0.28)", arrow: "▲" },
  SHORT:         { color: "#f87171", bg: "rgba(248,113,113,0.12)", border: "rgba(248,113,113,0.28)", arrow: "▼" },
  FADE_TOP:      { color: "#fbbf24", bg: "rgba(251,191,36,0.12)", border: "rgba(251,191,36,0.28)", arrow: "↘" },
  FADE_BOTTOM:   { color: "#fbbf24", bg: "rgba(251,191,36,0.12)", border: "rgba(251,191,36,0.28)", arrow: "↗" },
  WAIT:          { color: "#8b949e", bg: "rgba(139,148,158,0.10)", border: "rgba(139,148,158,0.20)", arrow: "○" },
};

export function SwingPlaysExpanded() {
  const [data, setData] = useState(() => {
    // localStorage SWR for instant render — same pattern as Elite/Crypto.
    try {
      const cached = localStorage.getItem("zi_swing_v1");
      if (cached) {
        const { payload, ts } = JSON.parse(cached);
        if (Date.now() - ts < 600000) return payload;
      }
    } catch {}
    return null;
  });
  const [loading, setLoading] = useState(() => {
    try { return !localStorage.getItem("zi_swing_v1"); } catch {}
    return true;
  });
  const [regime, setRegime] = useState(null);

  const load = async () => {
    try {
      const [r, reg] = await Promise.all([
        axios.get(`${API}/swing/next-day?top=8`),
        axios.get(`${API}/omega/market-regime`).catch(() => ({ data: null })),
      ]);
      if (r.data?.ok) {
        setData(r.data);
        try { localStorage.setItem("zi_swing_v1", JSON.stringify({ payload: r.data, ts: Date.now() })); } catch {}
      }
      if (reg.data?.ok) setRegime(reg.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); const iv = setInterval(load, 120000); return () => clearInterval(iv); }, []);

  const picks = data?.picks || [];

  return (
    <div style={{ padding: "0 16px 32px" }} data-testid="swing-plays-expanded">
      {/* Asymmetric header */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "4px 10px", borderRadius: 999, background: "rgba(167,139,250,0.10)", border: "1px solid rgba(167,139,250,0.25)", marginBottom: 10 }}>
          <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#a78bfa", animation: "pulse 2s infinite" }} />
          <span style={{ fontSize: 10, fontWeight: 800, color: "#a78bfa", letterSpacing: 2 }}>NEXT-DAY SWING · OMEGA ZI</span>
        </div>
        <div style={{ fontSize: 28, fontWeight: 800, color: "var(--text)", letterSpacing: -1, lineHeight: 1 }}>Tomorrow&apos;s Plays</div>
        <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}>Trend × momentum × RSI × ATR · concrete entry zones, stops, targets</div>
      </div>

      {/* Market regime banner */}
      {regime && (
        <div data-testid="swing-regime-banner" style={{
          display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 12,
          background: _regimeBg(regime.regime), border: `1px solid ${_regimeBorder(regime.regime)}`,
          marginBottom: 14,
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 10, fontWeight: 800, color: _regimeColor(regime.regime), letterSpacing: 1.5 }}>MARKET REGIME</span>
              <span style={{ fontSize: 12, fontWeight: 800, color: _regimeColor(regime.regime) }}>{regime.regime.replace("_", " ")}</span>
            </div>
            <div style={{ fontSize: 11, color: "var(--text)", marginTop: 3, lineHeight: 1.35 }}>{regime.note}</div>
          </div>
          <div style={{ textAlign: "right", flexShrink: 0 }}>
            <div style={{ fontSize: 9, color: "var(--muted)", letterSpacing: 1 }}>SIZE ×</div>
            <div style={{ fontSize: 16, fontWeight: 800, color: _regimeColor(regime.regime) }}>{regime.sizeMultiplier.toFixed(1)}</div>
          </div>
        </div>
      )}

      {loading && (<><Skel w="100%" h={100} mb={8} radius={12} /><Skel w="100%" h={100} mb={8} radius={12} /><Skel w="100%" h={100} mb={0} radius={12} /></>)}
      {!loading && picks.length === 0 && <div style={{ padding: 30, textAlign: "center", color: "var(--muted)", fontSize: 12 }}>No swing setups for tomorrow.</div>}

      {!loading && picks.map((p, i) => {
        const b = BIAS_STYLES[p.bias] || BIAS_STYLES.WAIT;
        const isActionable = p.bias !== "WAIT";
        return (
          <div
            key={p.ticker}
            data-testid={`swing-row-${p.ticker}`}
            onClick={() => hapticTap && hapticTap()}
            style={{
              marginBottom: 10, padding: "14px 14px",
              borderRadius: 14,
              background: "var(--card)",
              border: `1px solid ${isActionable ? b.border : "rgba(255,255,255,0.05)"}`,
              position: "relative", overflow: "hidden",
            }}
          >
            {/* Bias stripe on the left */}
            <div style={{ position: "absolute", top: 0, left: 0, bottom: 0, width: 3, background: b.color }} />

            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, paddingLeft: 6 }}>
              <span style={{ fontSize: 17, fontWeight: 800, color: "var(--text)", letterSpacing: -0.3 }}>{p.ticker}</span>
              <span style={{
                fontSize: 9, fontWeight: 800, padding: "3px 8px", borderRadius: 5,
                background: b.bg, color: b.color, letterSpacing: 1.2, border: `1px solid ${b.border}`,
              }}>
                {b.arrow} {p.bias.replace("_", " ")}
              </span>
              <span style={{ marginLeft: "auto", display: "flex", alignItems: "baseline", gap: 4 }}>
                <span style={{ fontSize: 9, color: "var(--muted)", letterSpacing: 1 }}>SCORE</span>
                <span style={{ fontSize: 14, fontWeight: 800, color: b.color, fontVariantNumeric: "tabular-nums" }}>{p.swingScore.toFixed(0)}</span>
              </span>
            </div>

            {isActionable && p.entryZone && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, paddingLeft: 6, marginBottom: 8 }}>
                <div>
                  <div style={{ fontSize: 9, color: "var(--muted)", letterSpacing: 1.2 }}>ENTRY</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text)", fontVariantNumeric: "tabular-nums" }}>
                    ${p.entryZone[0]}–${p.entryZone[1]}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: 9, color: "var(--muted)", letterSpacing: 1.2 }}>STOP</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#f87171", fontVariantNumeric: "tabular-nums" }}>${p.stop}</div>
                </div>
                <div>
                  <div style={{ fontSize: 9, color: "var(--muted)", letterSpacing: 1.2 }}>TARGET · {p.rrRatio}R</div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#4ade80", fontVariantNumeric: "tabular-nums" }}>${p.target}</div>
                </div>
              </div>
            )}

            <div style={{ paddingLeft: 6, fontSize: 10, color: "var(--muted)", lineHeight: 1.45 }}>
              ${p.price} · {p.rationale}
            </div>
          </div>
        );
      })}

      {!loading && data?.updated && (
        <div style={{ fontSize: 10, color: "var(--muted)", textAlign: "center", marginTop: 6, opacity: 0.6 }}>
          Updated {new Date(data.updated).toLocaleTimeString()}
        </div>
      )}
    </div>
  );
}

function _regimeColor(r) {
  if (r === "BULL" || r === "TRENDING_UP") return "#4ade80";
  if (r === "BEAR") return "#f87171";
  if (r === "VOLATILE") return "#fbbf24";
  if (r === "CHOP") return "#60a5fa";
  return "#a78bfa";
}
function _regimeBg(r) { return `${_regimeColor(r)}18`; }
function _regimeBorder(r) { return `${_regimeColor(r)}38`; }
